import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  getStoreWebsiteEditorStateV3,
  websiteTemplatesAdminKeys,
  WebsiteTemplateServiceError,
  type StoreWebsiteEditorStateV3,
} from '@dukanti/shared';
import { shouldRetry } from '@dukanti/shared/query';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The shared retry policy, except that a refused read (`permission_denied`,
 * staff) is final at once. The shared `shouldRetry` reads SQLSTATE classes
 * from `error.code`, but the website-template service error keeps the
 * SQLSTATE in `sqlState` and its `code` is `permission_denied`, so staff would
 * otherwise wait two backoff retries before the read-only notice shows.
 */
export function promotionalAdsEditorStateRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof WebsiteTemplateServiceError && error.code === 'permission_denied') {
    return false;
  }
  return shouldRetry(failureCount, error);
}

export interface PromotionalAdsEditorState {
  readonly query: UseQueryResult<StoreWebsiteEditorStateV3>;
  /** The explicit Retry: exactly one new read of a held error. */
  readonly retry: () => void;
}

/**
 * The entry-route editor state (`templateId` null) the Marketing gate reads.
 * Same query key, service and freshness as the shared
 * `useStoreWebsiteEditorStateV3(client, storeId, null)`, so it shares that
 * cache entry; only when it fetches differs.
 *
 * BUG-P7-1: an error with no data (staff `permission_denied`, or a load
 * failure after its retries) is held until the explicit `retry`. React Query
 * fetches an errored, data-less query whenever an observer mounts on it,
 * moves onto its key, or is enabled on it. The gate's store id starts null on
 * every mount (the session hydrates per hook), so the key moves from
 * `pending` to the store id each time; while the cached result for that key
 * is a held error the observer stays disabled, so none of those paths
 * re-queries it. `retry` releases that one held error: enabling the observer
 * runs exactly one read (with the usual retries); a new error is held again.
 * Mounts over cached data still refetch (`staleTime: 0`) and keep that data.
 */
export function usePromotionalAdsEditorState(
  client: SupabaseClient,
  storeId: string | null,
): PromotionalAdsEditorState {
  const queryClient = useQueryClient();
  const id = storeId?.trim() ?? '';
  const queryKey = websiteTemplatesAdminKeys.editorStateV3(id || 'pending', null);
  const cached = id === '' ? undefined : queryClient.getQueryState(queryKey);
  const heldErrorId =
    cached?.status === 'error' && cached.data === undefined
      ? `${id}:${cached.errorUpdatedAt}`
      : null;
  const [releasedErrorId, setReleasedErrorId] = useState<string | null>(null);
  const holdsError = heldErrorId !== null && heldErrorId !== releasedErrorId;

  const query = useQuery({
    queryKey,
    enabled: id !== '' && !holdsError,
    queryFn: () => getStoreWebsiteEditorStateV3(client, id, null),
    staleTime: 0,
    retryOnMount: false,
    retry: (failureCount: number, error: Error) =>
      promotionalAdsEditorStateRetry(failureCount, error),
  });

  const retry = useCallback(() => {
    if (heldErrorId !== null) setReleasedErrorId(heldErrorId);
    else void query.refetch();
  }, [heldErrorId, query]);

  return { query, retry };
}
